import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { DateTime } from "luxon"
import { Loading, Popup, ConfirmDialog } from "components"
import SampleRowElement from "./SampleRowElement"

import "./index.scss"

import { Button } from "react-bootstrap"
import InputGroup from "react-bootstrap/InputGroup"
import Form from "react-bootstrap/Form"
import Table from "react-bootstrap/Table"
import { Text, Container } from "components/Form"
import AddEditSamplePopup from "./AddEditSamplePopup"
import { FiInfo, FiAlertTriangle } from "react-icons/fi"
import { sampleView, samples, message } from "state_management"
import useT from "utils/hooks/useT"
import useLocalStorage from "utils/hooks/useLocalStorage"

import isOnMobile from "utils/scripts/isOnMobile"
import { dynamicSortMultiple } from "utils/scripts/sort"

export default function SampleView() {
  const dispatch = useDispatch()
  const { t } = useT("messages")
  const { t: tc } = useT("common")
  const isLoading = useSelector((state) => state.sampleView.isLoading)
  const needsRefresh = useSelector((state) => state.sampleView.needsRefresh)
  const sampleGroups = useSelector((state) => state.sampleView.sampleGroups)
  const sampleGroupsSortedByProduct = useSelector((state) => state.sampleView.sampleGroupsSortedByProduct)
  const teamTimeZone = useSelector((state) => state.teamInfo.timezone)
  const members = useSelector((state) => state.members)
  const isViewOnly = useSelector((state) => state.auth.isViewOnly)
  const isMember = useSelector((state) => state.auth.isMember)
  const allowTaskTransfer = useSelector((state) => state.teamStatus.allowTaskTransfer)
  const isReadOnly = isViewOnly || (isMember && !allowTaskTransfer)
  const shouldBeDoneSamples = useSelector((state) => state.sampleView.shouldBeDoneSamples)
  const analysisDefinitions = useSelector((state) => state.analysisDefinitions)
  const productCollection = useMemo(() => {
    const set = new Set()
    ;(sampleGroups || []).forEach((s) => set.add(s.productName || ""))
    return Array.from(set).map((p) => ({ id: p, name: p === "" ? tc("samplesWithoutProduct") : p }))
  }, [sampleGroups, tc])

  // Build collection for analyses used in the Add Sample popup
  const analysisCollection = useMemo(() => {
    const defs = analysisDefinitions || {}
    return Object.values(defs).map((a) => ({
      // prefer explicit id fields (analysisId or id) so selects return ids instead of names
      id: a.analysisId || a.id || a.name || a.analysis_name || a.analysisName,
      name: a.display_name || a.displayName || a.name || a.analysis_name || a.analysisName || String(a.id),
    }))
  }, [analysisDefinitions])

  const [filterString, setFilterString] = useState("")
  const [failConfirm, setFailConfirm] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [pendingDeleteSampleId, setPendingDeleteSampleId] = useState(null)

  const [activeSample, setActiveSample] = useState("")
  const [samplePriority, setSamplePriority] = useState(null)
  const [activeSampleAnalysis, setActiveSampleAnalysis] = useState({})
  const [sortByProduct, setSortByProduct] = useLocalStorage("sampleView-sortByProduct", false)
  const [includeDone, setIncludeDone] = useLocalStorage("sampleView-includeDone", false)
  const sampleViewRef = useRef(null);
  const shouldShowUnplannedPopup = Object.values(activeSampleAnalysis).length !== 0 && !activeSampleAnalysis?.analysesPlanned;

  // Add-sample popup state and form
  const [showAddSample, setShowAddSample] = useState(false)
  // showMore removed - we don't need local expanded state in this file now
  const [newSample, setNewSample] = useState({
    sampleName: "",
    analysisName: [],
    observationId: "1",
    numberOfObservations: 1,
    arrival: DateTime.now().setZone(teamTimeZone).startOf('day').toISO(),
    arrivalTime: "00:00",
    // Keep date undefined until user sets it, but default time visually to 23:59
    deadline: "",
    deadlineTime: "23:59",
    priority: 2,
    status: "TODO",
    productName: "",
    exclusive: false,
    isFlagged: false,
  })

  // submitNewSample removed; new flow uses AddEditSamplePopup and submitNewSampleFromPopup

  const resetNewSampleForm = () => {
    setShowAddSample(false)
    setNewSample({
      sampleName: "",
      analysisName: [],
      observationId: "1",
      numberOfObservations: 1,
      arrival: DateTime.now().setZone(teamTimeZone).startOf('day').toISO(),
      arrivalTime: "00:00",
      deadline: "",
      deadlineTime: "23:59",
      priority: 2,
      status: "TODO",
      productName: null,
      exclusive: false,
      uploadConfigId: "",
      isFlagged: false,
    })
  }

  const submitNewSampleFromPopup = (payload) => {
    // payload comes from AddEditSamplePopup: arrival/deadline already ISO strings
    // We expect camelCase keys from the popup: analysisIds, observationId
    const rawAnalyses = payload.analysisIds || []
    const hasAnalysis = Array.isArray(rawAnalyses) ? rawAnalyses.filter(Boolean).length > 0 : !!rawAnalyses
    if (!payload.sampleName || !hasAnalysis || !payload.arrival || !payload.deadline) {
      console.log('submitNewSampleFromPopup: missing fields', { sampleName: payload.sampleName, hasAnalysis, arrival: payload.arrival, deadline: payload.deadline, rawAnalyses })
      dispatch(message.warning(t("adhoc.fillRequiredFields")))
      return
    }

    // Ensure analysisIds contain only ids. Map any names to ids using analysisCollection if necessary
    const analysisIds = (Array.isArray(rawAnalyses) ? rawAnalyses : [rawAnalyses])
      .filter(Boolean)
      .map((v) => {
        const foundById = analysisCollection.find((ac) => String(ac.id) === String(v))
        if (foundById) return foundById.id
        const foundByName = analysisCollection.find((ac) => ac.name === v || String(ac.name) === String(v))
        if (foundByName) return foundByName.id
        return v
      })

    const input = {
      sampleName: payload.sampleName,
      analysisIds: analysisIds,
      observationId: payload.observation_id || payload.observationId || newSample.observationId,
      numberOfObservations: payload.numberOfObservations || newSample.numberOfObservations,
      arrival: payload.arrival,
      deadline: payload.deadline,
      priority: parseInt(payload.priority, 10),
      status: payload.status,
      productName: payload.productName || null,
      exclusive: !!payload.exclusive,
      isFlagged: !!payload.isFlagged,
    }

    dispatch(samples.createSample(input)).then(() => {
      setShowAddSample(false)
      setNewSample({
        sampleName: "",
        analysisName: [],
        observationId: "1",
        numberOfObservations: 1,
        arrival: DateTime.now().setZone(teamTimeZone).startOf('day').toISO(),
        arrivalTime: "00:00",
        deadline: "",
        deadlineTime: "23:59",
        priority: 2,
        status: "TODO",
        productName: null,
        exclusive: false,
        uploadConfigId: "",
        isFlagged: false,
      })
    }).catch(() => {})
  }

  useEffect(() => {
    if (needsRefresh) {
      dispatch(sampleView.fetch())
    }
  }, [needsRefresh, dispatch])

  useEffect(() => {
    let timer;
    if (shouldShowUnplannedPopup) {
      // Set the timeout to hide the popup
      timer = setTimeout(() => {
        setActiveSampleAnalysis({});
      }, 4000);

      const handleScroll = () => {
        setActiveSampleAnalysis({});
        clearTimeout(timer);
      };

      // Ensure the element exists before adding the event listener
      const element = sampleViewRef.current;
      if (element) {
        element.addEventListener('scroll', handleScroll, { passive: true });
      }

      // Cleanup function
      return () => {
        clearTimeout(timer);
        if (element) {
          element.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [shouldShowUnplannedPopup]);

  const filteredSampleGroups = useMemo(() => {
    // Ensure the arrays are not null before proceeding
    if (!sampleGroups || !sampleGroupsSortedByProduct) {
      return [];
    }

    // Filter the sample groups based on the filterString and remove null values
    let sortedSampleGroups = (!sortByProduct ? sampleGroups : sampleGroupsSortedByProduct)
    .map((product) => {
      if (!sortByProduct) {
        return filterString ? (product.sampleName.toUpperCase().includes(filterString.toUpperCase()) ? product : null) : product;
      } else {
        const [key, value] = Object.entries(product)[0];
        const filteredSamples = key.toUpperCase().includes(filterString.toUpperCase())
          ? value
          : value.filter((sample) => sample.sampleName.toUpperCase().includes(filterString.toUpperCase()));
        const isAllDone = filteredSamples.every((sample) => sample.isDone);
        if (!includeDone && isAllDone) {
          return null;
        }
        return filteredSamples.length > 0
          ? { [key]: filteredSamples.filter((sample) => includeDone || !sample.isDone) }
          : null;
      }
    })
    .filter((product) => product !== null);

    // Sort the filtered sample groups by minDeadline and sampleName
    if (!sortByProduct) {
      // Sort the whole array by sampleName if not sorting by product
      sortedSampleGroups = sortedSampleGroups.sort(dynamicSortMultiple('minDeadline', 'sampleName'));
    } else {
      // When sorting by product, sort each product group's samples
      sortedSampleGroups = sortedSampleGroups.map((group) => {
        const [productName, samples] = Object.entries(group)[0];

        // Sort the samples within this group
        const sortedSamples = samples.sort(dynamicSortMultiple('minDeadline', 'sampleName'));

        // Return the sorted group with the product name key
        return { [productName]: sortedSamples };
      });
    }

    return sortedSampleGroups;
  }, [sampleGroups, sampleGroupsSortedByProduct, sortByProduct, filterString, includeDone]);

  if (isLoading || needsRefresh) {
    return <Loading title={tc("loadingSamples")} />
  }

  const initialValue = 0
  // Compute width of blue-box. Need to take width of deadline and icons into account: '70px' and '85px'
  const maxWidth =
    "min(calc(" +
      sampleGroups.reduce((acc, curr) => Math.max(curr.sampleName.length, acc), initialValue) +
      "rem + 70px + 85px), 33%)"


  const handleFlagSample = () => {
    dispatch(samples.flagSample(activeSampleAnalysis.sampleId, activeSampleAnalysis.sampleName))
    setActiveSampleAnalysis({})
  }

  const handleUnflagSample = () => {
    dispatch(samples.unflagSample(activeSampleAnalysis.sampleId, activeSampleAnalysis.sampleName))
    setActiveSampleAnalysis({})
  }

  if (sampleGroups.length !== 0) {

    return (
      <>
        <div className="SampleView" ref={sampleViewRef}>
          <div className="sampleView-overhead-container">
            <div className="sampleview-filter-box" style={{ width: `calc(${maxWidth} - 3rem)` }}>
              <InputGroup>
                <Text
                  placeholder={sortByProduct ? tc("filterBySampleProductName") : tc("filterBySampleName")}
                  value={filterString}
                  onChange={setFilterString}
                />
              </InputGroup>
            </div>
            {shouldBeDoneSamples.length > 0 && (<FiAlertTriangle className="alert-triangle" title={tc("sampleInProgressOnDoneAnalysis")} />)}
            <div className="mb-3 sampleview-checkbox">
              <Form.Check
                checked={sortByProduct}
                type={"checkbox"}
                id={`sort-by-product`}
                label={tc("sortByProduct")}
                onChange={(e) => setSortByProduct(!sortByProduct)}
              />
              <Form.Check
                checked={includeDone}
                type={"checkbox"}
                id={`include-done-samples`}
                label={tc("includeDoneSamples")}
                onChange={(e) => setIncludeDone(!includeDone)}
              />
            </div>
            {!isReadOnly && (
              <div>
                <Button variant="success" onClick={() => setShowAddSample(true)}>
                  {isOnMobile ? tc("add") : tc("addSample")}
                </Button>
              </div>
            )}
          </div>

          {showAddSample && (
            <Popup onCancel={() => { resetNewSampleForm() }}>
              <AddEditSamplePopup
                title={tc("addSample")}
                initial={{
                  sampleName: newSample.sampleName,
                  analysisName: newSample.analysisName,
                  observationId: newSample.observationId,
                  arrival: newSample.arrival,
                  deadline: newSample.deadline ? `${newSample.deadline}T${newSample.deadlineTime}` : "",
                  priority: newSample.priority,
                  status: newSample.status,
                  productName: newSample.productName,
                  exclusive: newSample.exclusive,
                  isFlagged: newSample.isFlagged,
                }}
                analysisCollection={analysisCollection}
                productCollection={productCollection}
                onCancel={() => { resetNewSampleForm() }}
                onSubmit={(payload) => { submitNewSampleFromPopup(payload) }}
              />
            </Popup>
          )}

          {filteredSampleGroups.length !== 0 && !sortByProduct ? (
            filteredSampleGroups.flatMap((sample) => {
              if (!includeDone && sample.isDone) {
                return [] // skip this sample entirely
              }
              // Split analyses by observationId and create one row per observation id
              const analysesByObs = (sample.analyses || []).reduce((acc, a) => {
                const oid = a.observationId || "1"
                if (!acc[oid]) acc[oid] = []
                acc[oid].push(a)
                return acc
              }, {})

              return Object.entries(analysesByObs).map(([oid, analyses], index) => (
                <SampleRowElement
                  key={`${sample.sampleName}-${sample.productName || 'unknown'}-obs-${oid}-${index}`}
                  rowId={`${sample.sampleName}-${sample.productName || 'unknown'}-obs-${oid}-${index}`}
                  group={{ ...sample, analyses: analyses, observationId: oid, baseSampleName: sample.sampleName, sampleName: `${sample.sampleName}` }}
                  maxWidth={maxWidth}
                  activeSample={activeSample}
                  samplePriority={samplePriority}
                  filteredSampleGroups={filteredSampleGroups}
                  setActiveSampleAnalysis={setActiveSampleAnalysis}
                  setActiveSample={setActiveSample}
                  setSamplePriority={setSamplePriority}
                  analysisCollection={analysisCollection}
                  productCollection={productCollection}
                  isReadOnly={isReadOnly}
                />
              ))
            })
          ) : filteredSampleGroups.length !== 0 && sortByProduct ? (
              filteredSampleGroups.map((product) => {
                const currentProductSamples = product[Object.keys(product)[0]]
                return (
                  <React.Fragment key={Object.keys(product)[0]}>
                    <h3>{Object.keys(product)[0] === "null" ? tc("samplesWithoutProduct") : Object.keys(product)[0]}</h3>
                    {currentProductSamples.flatMap((sample) => {
                      // split sample by observation id
                      const analysesByObs = (sample.analyses || []).reduce((acc, a) => {
                        const oid = a.observationId || "1"
                        if (!acc[oid]) acc[oid] = []
                        acc[oid].push(a)
                        return acc
                      }, {})

                      return Object.entries(analysesByObs).map(([oid, analyses], index) => (
                        <SampleRowElement
                          key={`${sample.sampleName}-${sample.productName || 'unknown'}-obs-${oid}-${index}`}
                          rowId={`${sample.sampleName}-${sample.productName || 'unknown'}-obs-${oid}-${index}`}
                          group={{ ...sample, analyses: analyses, observationId: oid, baseSampleName: sample.sampleName, sampleName: `${sample.sampleName}` }}
                          maxWidth={maxWidth}
                          activeSample={activeSample}
                          samplePriority={samplePriority}
                          filteredSampleGroups={sampleGroups}
                          setActiveSampleAnalysis={setActiveSampleAnalysis}
                          setActiveSample={setActiveSample}
                          setSamplePriority={setSamplePriority}
                          isReadOnly={isReadOnly}
                        />
                      ))
                    })}
                  </React.Fragment>
                )
              })
            ) : (
                <h3>{tc("noSearchResults")}</h3>
              )}

          {Object.values(activeSampleAnalysis).length !== 0 && (
            <>
              {activeSampleAnalysis?.analysesPlanned ? (
                <Popup
                  onCancel={() => {
                    if (!failConfirm) {
                      setActiveSampleAnalysis({})
                    }
                  }}
                  className="sampleview-task-table-popup"
                  center
                >
                  <div className="sampleview-task-table-popup-content">
                    <h4 className="sampleview-task-title">{activeSampleAnalysis.name}</h4>
                    <div className="sampleview-task-main">
                      <Table bordered hover className="SampleTable">
                        <thead>
                          <tr>
                            <th>{tc("tasks")}</th>
                            <th>{tc("collaborator")}</th>
                            <th>{tc("startEndDate")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeSampleAnalysis.tasks.map((task, idx) => (
                            <tr key={idx}>
                              <td>{task.name}</td>
                              <td>{task.collaborators.length !== 0 ? members[task.collaborators[0]].initials : ""}</td>
                              <td>
                                <ul>
                                  <li>{DateTime.fromISO(task.start).setZone(teamTimeZone).toFormat("ff")}</li>
                                  <li>{DateTime.fromISO(task.end).setZone(teamTimeZone).toFormat("ff")}</li>
                                </ul>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <div>
                      <h5 className="sampleview-task-title">{tc("sampleIdWithValue", { id: activeSampleAnalysis.observationId })}</h5>
                      {activeSampleAnalysis.customerName && (
                        <p>{tc("customerName")}: {activeSampleAnalysis.customerName}</p>
                      )}
                      {!isReadOnly && (
                        <div className="sampletable-buttons">
                          {(activeSampleAnalysis && activeSampleAnalysis.manual === true && !(activeSampleAnalysis.isDone === true || String(activeSampleAnalysis.status) === 'DONE')) && (
                            <Button
                              variant="success"
                              onClick={() => {
                                // Mark this planned sample observation as DONE
                                const sid = activeSampleAnalysis.sampleId || activeSampleAnalysis.id || activeSampleAnalysis.sample_id || activeSampleAnalysis.sample?.id
                                if (!sid) {
                                  dispatch(message.warning(t("adhoc.couldNotDetermineSampleIdDone")))
                                  return
                                }
                                dispatch(sampleView.markSampleDone(sid))
                                setActiveSampleAnalysis({})
                              }}
                            >
                              {isOnMobile ? tc("done") : tc("markAsDone")}
                            </Button>
                          )}
                          <Button
                            variant="danger"
                            onClick={() => {
                              dispatch(samples.unplanSamples(activeSampleAnalysis.analysisId, [activeSampleAnalysis.sampleId]))
                              setActiveSampleAnalysis({})
                            }}
                          >
                            {isOnMobile ? tc("unplan") : tc("unplanSample")}
                          </Button>
                          <Button onClick={() => setFailConfirm(true)}>{isOnMobile ? tc("fail") : tc("failSample")}</Button>
                          {!activeSampleAnalysis.isFlagged ? (
                            <Button onClick={handleFlagSample}>
                              {isOnMobile ? tc("flag") : tc("flagSample")}
                            </Button>
                          ) : (
                              <Button onClick={handleUnflagSample}>
                                {isOnMobile ? tc("unflag") : tc("unflagSample")}
                              </Button>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              ) : (
                  <>
                    {shouldShowUnplannedPopup && (
                      <>
                        <Popup
                          singleMessage={
                            <>
                              <p>
                                <b>{activeSampleAnalysis.name}</b>
                              </p>
                              <p>{tc("sampleIdWithValue", { id: activeSampleAnalysis.observationId })}</p>
                              <p>{tc("analysisNotPlanned")}</p>
                              { !isReadOnly && (activeSampleAnalysis && (activeSampleAnalysis.manual === true || String(activeSampleAnalysis.manual) === 'true')) && (
                                <div style={{ marginTop: '0.5rem' }}>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => {
                                      // compute id immediately and store so it doesn't vanish if activeSampleAnalysis is cleared
                                      const sid = activeSampleAnalysis && (
                                        activeSampleAnalysis.sampleId ||
                                        activeSampleAnalysis.id ||
                                        activeSampleAnalysis.sample_id ||
                                        activeSampleAnalysis.sample?.id ||
                                        activeSampleAnalysis.observationId ||
                                        null
                                      )
                                      if (!sid) {
                                        dispatch(message.warning(t("adhoc.couldNotDetermineSampleIdDelete")))
                                        return
                                      }
                                      setPendingDeleteSampleId(sid)
                                      setDeleteConfirm(true)
                                    }}
                                  >
                                    {isOnMobile ? tc("delete") : tc("deleteSample")}
                                  </Button>
                                </div>
                              )}
                            </>
                          }
                          onCancel={() => setActiveSampleAnalysis({})}
                          position={{
                            top: activeSampleAnalysis.position?.top - 180,
                            left: activeSampleAnalysis.position?.left,
                            height: activeSampleAnalysis.position?.height,
                          }}
                        />
                      </>
                    )}
                  </>
                )}
            </>
          )}
          {failConfirm && (
            <ConfirmDialog
              onCancel={() => {
                setFailConfirm(false)
              }}
              onConfirm={() => {
                dispatch(
                  samples.failSamples(
                    activeSampleAnalysis.analysisId,
                    [activeSampleAnalysis.sampleId],
                    true,
                    activeSampleAnalysis.sampleName,
                    activeSampleAnalysis.productName,
                    activeSampleAnalysis.observationId
                  )
                )
                setActiveSampleAnalysis({})
                setFailConfirm(false)
              }}
            >
              <Container>
                <div className="FailSamples-info-text">
                  <FiInfo title={tc("confirmFailSamplesInfo")} />
                </div>
                <h4>{tc("confirmFailSamples")}</h4>
              </Container>
            </ConfirmDialog>
          )}
          {deleteConfirm && (
            <ConfirmDialog
              onCancel={() => {
                setDeleteConfirm(false)
                setPendingDeleteSampleId(null)
              }}
              onConfirm={() => {
                const sid = pendingDeleteSampleId || (activeSampleAnalysis && (
                  activeSampleAnalysis.sampleId ||
                  activeSampleAnalysis.id ||
                  activeSampleAnalysis.sample_id ||
                  activeSampleAnalysis.sample?.id ||
                  activeSampleAnalysis.observationId ||
                  null
                ))
                if (!sid) {
                  dispatch(message.warning(t("adhoc.couldNotDetermineSampleIdDeleteMissing")))
                  setDeleteConfirm(false)
                  setPendingDeleteSampleId(null)
                  setActiveSampleAnalysis({})
                  return
                }
                dispatch(sampleView.deleteSample(sid)).then(() => {
                  setActiveSampleAnalysis({})
                }).catch(() => {})
                setDeleteConfirm(false)
                setPendingDeleteSampleId(null)
              }}
              submitName={isOnMobile ? tc("delete") : tc("deleteSample")}
            >
              <Container>
                <div className="FailSamples-info-text">
                  <FiInfo title={tc("confirmDeleteSampleInfo")} />
                </div>
                <h4>{tc("confirmDeleteSample")}</h4>
              </Container>
            </ConfirmDialog>
          )}
        </div>
      </>
    )
  } else {
    return (
      <div className="SampleView">
        <h3>{tc("noSamplesAvailable")}</h3>
      </div>
    )
  }
}
